import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, ArrowLeft, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { getPaymentStatus } from '@/lib/api';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const checkStatus = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      try {
        const data = await getPaymentStatus(sessionId);
        setStatus(data);
        
        if (data.payment_status === 'paid') {
          setLoading(false);
        } else if (attempts < 5) {
          // Poll for status if not yet paid
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to check payment status:', error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setLoading(false);
        }
      }
    };

    checkStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="payment-success-page">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 text-center">
          {loading ? (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
              <h2 className="font-heading text-xl font-semibold mb-2">Processing Payment...</h2>
              <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
            </>
          ) : status?.payment_status === 'paid' ? (
            <>
              <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-secondary" />
              </div>
              <h2 className="font-heading text-2xl font-semibold mb-2">Payment Successful!</h2>
              <p className="text-muted-foreground mb-6">
                Your subscription is now active. Your listing will appear in the directory.
              </p>
              <div className="bg-stone-50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-muted-foreground mb-1">Amount Paid</p>
                <p className="font-semibold text-lg">
                  ${(status.amount_total / 100).toFixed(2)} {status.currency?.toUpperCase()}
                </p>
              </div>
              <Link to="/directory">
                <Button className="w-full bg-primary hover:bg-primary/90">
                  View Directory
                </Button>
              </Link>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold mb-2">Payment Status Unknown</h2>
              <p className="text-muted-foreground mb-6">
                We couldn't confirm your payment status. If you completed the payment, your subscription should be activated shortly.
              </p>
              <Link to="/">
                <Button className="w-full" variant="outline">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
